import type { OutreachHold } from '@/modules/hiring-manager/utils/outreachReadiness';
import type { JobApi } from '@/modules/jobs/types';
import type { JobAutomation } from '@/pages/hm/job-automation/jobAutomation';

/**
 * Where a step stands: Finlay is `running` it, has it `done`, is `blocked`
 * until the user acts, or owns it but is `idle` now; `you` handle it.
 */
export type ActivityStepState = 'running' | 'done' | 'blocked' | 'idle' | 'you';

export interface ActivityStep {
  name: string;
  state: ActivityStepState;
  /** One sentence on what happens in the step. */
  detail: string;
}

export interface JobActivity {
  /** What Finlay is doing on the job now; `idle` when no step runs. */
  status: { tone: 'working' | 'idle'; text: string } | null;
  /** Steps that should run but are stopped until the user acts. */
  attention: string[];
  /** A few words for the job's row: the first blocker, else what runs, else why nothing does. */
  summary: { tone: 'working' | 'attention' | 'idle'; text: string } | null;
  /** The job's flow up to the user's decision, in pipeline order. */
  steps: ActivityStep[];
}

interface JobActivityInput {
  job: Pick<JobApi, 'state' | 'sourcingState'>;
  /** What the plan actually runs, not only what the job's flags ask for. */
  automation: JobAutomation;
  /** Why Finlay's outreach waits for LinkedIn (`outreachHold`); null when it does not. */
  outreachHold: OutreachHold | null;
}

const searchingStates: JobApi['sourcingState'][] = [
  'New',
  'Targeting',
  'InProgress',
];

const listOf = (items: string[]) =>
  items.length < 2
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;

const capitalize = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Outreach and the pre-screen run only on a published job, as the Automation
 * tab's `isLive` (HMJobAutomation.tsx) assumes; an unpublished job may
 * already be sourcing.
 */
const isLiveFlow = (job: JobActivityInput['job']) => job.state === 'Published';

/** Each step of the job's flow and where it stands; a paused job runs none. */
const stepsOf = ({
  job,
  automation,
  outreachHold,
}: JobActivityInput): ActivityStep[] => {
  const isPaused = job.state === 'OnHold';
  const isLive = isLiveFlow(job);
  const finlay = (detail: string): Pick<ActivityStep, 'state' | 'detail'> => ({
    state: isPaused ? 'idle' : 'running',
    detail,
  });
  // Outreach and the pre-screen wait for publishing; sourcing may not.
  const flow = (detail: string): Pick<ActivityStep, 'state' | 'detail'> => ({
    state: isLive ? 'running' : 'idle',
    detail,
  });
  // Pausing wins, as in the row summary: a paused job runs nothing, so
  // nothing in it is held up either.
  const blocked = (detail: string): Pick<ActivityStep, 'state' | 'detail'> => ({
    state: isPaused ? 'idle' : 'blocked',
    detail,
  });
  const steps: ActivityStep[] = [
    {
      name: 'Sourcing',
      ...(job.sourcingState === 'Finished'
        ? { state: 'done', detail: 'Search complete.' }
        : job.sourcingState === 'Failed'
          ? blocked('The search stopped before it finished.')
          : job.sourcingState === 'Draft'
            ? { state: 'idle', detail: 'The search has not started yet.' }
            : finlay('Finding and qualifying matching candidates.')),
    },
    {
      name: 'Outreach',
      ...(automation.outreach === 'me'
        ? { state: 'you', detail: 'You contact the people you like.' }
        : outreachHold && isLive
          ? blocked(outreachHold.text)
          : flow(
              'Messaging matching candidates and following up until they reply.',
            )),
    },
  ];
  steps.push(
    {
      name: 'Pre-screen',
      ...(automation.prescreen === 'ai'
        ? flow(
            'Holding a short AI voice intro with each candidate, covering your must-haves.',
          )
        : automation.prescreen === 'human'
          ? {
              state: 'you',
              detail: 'Candidates book a screening call with you.',
            }
          : { state: 'you', detail: 'You screen candidates yourself.' }),
    },
    // Interview scheduling is not on the platform yet, so the flow ends
    // with the user's decision.
    { name: 'Decision', state: 'you', detail: 'You choose who moves forward.' },
  );
  return steps;
};

/** The row's few words, from the first step that needs the user. */
const summaryOf = (
  { job, outreachHold }: JobActivityInput,
  steps: ActivityStep[],
  running: string[],
): JobActivity['summary'] => {
  if (job.state === 'OnHold') return { tone: 'idle', text: 'Paused' };
  const blocked = steps.find((step) => step.state === 'blocked');
  if (blocked?.name === 'Sourcing') {
    return { tone: 'attention', text: 'Sourcing failed' };
  }
  // Only a hold blocks the Outreach step.
  if (blocked && outreachHold) {
    return { tone: 'attention', text: outreachHold.summary };
  }
  if (running.length > 0) {
    return { tone: 'working', text: capitalize(running.join(', ')) };
  }
  return job.sourcingState === 'Finished'
    ? { tone: 'idle', text: 'Sourcing complete' }
    : null;
};

/**
 * What Finlay is doing on a job, for the portal home: the steps it is
 * running, named without numbers, what stopped the others, and who handles
 * each step.
 */
export const getJobActivity = (input: JobActivityInput): JobActivity => {
  const { job, automation, outreachHold } = input;
  const steps = stepsOf(input);

  if (job.state === 'OnHold') {
    return {
      status: { tone: 'idle', text: 'Paused. Finlay is not working on it.' },
      attention: [],
      summary: summaryOf(input, steps, []),
      steps,
    };
  }

  const attention: string[] = [];
  const running: string[] = [];

  if (searchingStates.includes(job.sourcingState)) running.push('sourcing');
  if (job.sourcingState === 'Failed') attention.push('Sourcing failed.');

  if (automation.outreach === 'finlay' && isLiveFlow(job)) {
    if (outreachHold) {
      attention.push(outreachHold.text);
    } else {
      running.push('reaching out');
    }
  }

  if (automation.prescreen === 'ai' && isLiveFlow(job)) {
    running.push('pre-screening');
  }

  if (running.length === 0) {
    return {
      status:
        job.sourcingState === 'Finished'
          ? { tone: 'idle', text: 'Finlay finished sourcing.' }
          : null,
      attention,
      summary: summaryOf(input, steps, running),
      steps,
    };
  }

  const object =
    running.at(-1) === 'reaching out' ? 'to candidates' : 'candidates';
  // Sourcing only hands every candidate over anyway; the hand-off is only
  // worth naming when Finlay picks the work up again after the user's outreach.
  const handOff =
    automation.outreach === 'me' && running.includes('pre-screening')
      ? ' You handle outreach.'
      : '';

  return {
    status: {
      tone: 'working',
      text: `Finlay is ${listOf(running)} ${object}.${handOff}`,
    },
    attention,
    summary: summaryOf(input, steps, running),
    steps,
  };
};
